import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNotifications } from '../context/NotificationContext';

interface AutoPrintNotificationProps {
  kotNumber: string;
  tableInfo: string;
  itemCount: number;
  onClose: () => void;
}

const AutoPrintNotification: React.FC<AutoPrintNotificationProps> = ({
  kotNumber,
  tableInfo,
  itemCount,
  onClose
}) => {
  const { theme } = useTheme();
  const { addNotification } = useNotifications();
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Add to notification history only once when component mounts
    addNotification({
      type: 'kot',
      title: 'KOT Printed',
      message: `${kotNumber} for ${tableInfo} (${itemCount} items) sent to printer`,
      data: { kotNumber, tableInfo, itemCount }
    });
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    // Start fade out timer
    const duration = 5000; // 5 seconds
    const interval = 50; // Update every 50ms
    const step = (100 / duration) * interval;

    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - step;
        if (newProgress <= 0) {
          setIsVisible(false);
          setTimeout(onClose, 300); // Allow fade animation
          return 0;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{
        backgroundColor: theme.components.card.background + 'f0',
        backdropFilter: 'blur(10px)',
        boxShadow: `0 10px 40px ${theme.primary}30`,
        border: `1px solid ${theme.components.card.border}`,
        borderRadius: '12px'
      }}
    >
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="text-2xl sm:text-3xl animate-pulse flex-shrink-0">🖨️</div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm" style={{ color: theme.text }}>
              Printing KOT
            </h4>
            <p className="text-xs truncate" style={{ color: theme.gray[600] }}>
              {kotNumber} • {tableInfo} • {itemCount} items
            </p>
            <p className="text-xs mt-1" style={{ color: theme.success }}>
              ✓ Sent to printer automatically
            </p>
          </div>
          <button
            onClick={() => {
              setIsVisible(false);
              setTimeout(onClose, 300);
            }}
            className="text-xs p-1 rounded-full transition-colors hover:bg-gray-200"
            style={{ color: theme.gray[500] }}
          >
            ✕
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-100"
            style={{
              width: `${progress}%`,
              backgroundColor: theme.primary
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AutoPrintNotification;